import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../utils/api'
import { MetricsRow } from '../shared'
import '../../styles/pages.css'

/**
 * Recursive tree node component
 */
function CategoryNode({
  node,
  depth,
  expandedIds,
  onToggleExpand,
  onEdit,
  onAddChild,
  onMove,
  onDelete,
  onViewProducts,
  allCategories,
  editingId,
  setEditingId,
}) {
  const [editName, setEditName] = useState(node.name)
  const [showMoveDropdown, setShowMoveDropdown] = useState(false)
  const [flipDropdown, setFlipDropdown] = useState(false)
  const moveButtonRef = useRef(null)
  const isExpanded = expandedIds.has(node.category_id)
  const hasChildren = node.children && node.children.length > 0
  const isEditing = editingId === node.category_id

  // Get valid move targets (exclude self and descendants)
  const getValidMoveTargets = useCallback(() => {
    const invalidIds = new Set()

    // Recursively collect all descendant IDs
    function collectDescendants(cat) {
      invalidIds.add(cat.category_id)
      if (cat.children) {
        cat.children.forEach(collectDescendants)
      }
    }
    collectDescendants(node)

    // Flatten all categories and filter out invalid targets
    function flatten(categories) {
      let result = []
      for (const cat of categories) {
        if (!invalidIds.has(cat.category_id)) {
          result.push(cat)
        }
        if (cat.children) {
          result = result.concat(flatten(cat.children))
        }
      }
      return result
    }

    return flatten(allCategories)
  }, [node, allCategories])

  function handleEditSubmit(e) {
    e.preventDefault()
    if (editName.trim() && editName.trim() !== node.name) {
      onEdit(node.category_id, editName.trim())
    }
    setEditingId(null)
  }

  function handleEditCancel() {
    setEditName(node.name)
    setEditingId(null)
  }

  function handleMoveSelect(newParentId) {
    onMove(node.category_id, newParentId)
    setShowMoveDropdown(false)
  }

  function handleMoveToggle() {
    if (!showMoveDropdown && moveButtonRef.current) {
      const rect = moveButtonRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      // Flip if button is in lower 40% of viewport
      setFlipDropdown(rect.bottom > viewportHeight * 0.6)
    }
    setShowMoveDropdown(!showMoveDropdown)
  }

  return (
    <div className="category-node">
      <div
        className="category-row"
        style={{ paddingLeft: `${depth * 24}px` }}
      >
        {/* Expand/collapse toggle */}
        <button
          type="button"
          className="category-toggle"
          onClick={() => hasChildren && onToggleExpand(node.category_id)}
          disabled={!hasChildren}
        >
          {hasChildren ? (isExpanded ? '▼' : '▶') : '•'}
        </button>

        {/* Name (editable) */}
        {isEditing ? (
          <form onSubmit={handleEditSubmit} className="category-edit-form">
            <input
              type="text"
              className="form-input form-input-sm"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              autoFocus
              onBlur={handleEditCancel}
              onKeyDown={e => e.key === 'Escape' && handleEditCancel()}
            />
          </form>
        ) : (
          <span
            className="category-name"
            onClick={() => hasChildren && onToggleExpand(node.category_id)}
            onDoubleClick={() => setEditingId(node.category_id)}
          >
            {node.name}
          </span>
        )}

        {/* Product counts */}
        <span className="category-counts">
          {hasChildren ? (
            <span
              className="badge badge-info badge-clickable"
              title="Click to view products in this category"
              onClick={() => onViewProducts(node.category_id, node.name)}
            >
              {node.recursive_product_count}
            </span>
          ) : (
            <span
              className="badge badge-neutral badge-clickable"
              title="Click to view products in this category"
              onClick={() => onViewProducts(node.category_id, node.name)}
            >
              {node.direct_product_count}
            </span>
          )}
        </span>

        {/* Actions */}
        <div className="category-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setEditingId(node.category_id)}
            title="Edit name"
          >
            Edit
          </button>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onAddChild(node.category_id)}
            title="Add child category"
          >
            + Child
          </button>

          <div className="dropdown-wrapper" onMouseLeave={() => setShowMoveDropdown(false)}>
            <button
              ref={moveButtonRef}
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleMoveToggle}
              title="Move to different parent"
            >
              Move
            </button>
            {showMoveDropdown && (
              <div className={`dropdown-menu ${flipDropdown ? 'dropdown-menu-up' : ''}`}>
                <div
                  className="dropdown-item"
                  onClick={() => handleMoveSelect(null)}
                >
                  (Top level)
                </div>
                {getValidMoveTargets().map(target => (
                  <div
                    key={target.category_id}
                    className="dropdown-item"
                    onClick={() => handleMoveSelect(target.category_id)}
                  >
                    {target.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => onDelete(node)}
            title="Delete category"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="category-children">
          {node.children.map(child => (
            <CategoryNode
              key={child.category_id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onMove={onMove}
              onDelete={onDelete}
              onViewProducts={onViewProducts}
              allCategories={allCategories}
              editingId={editingId}
              setEditingId={setEditingId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function PIMCategories() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Stats
  const [stats, setStats] = useState({
    brand_count: 0,
    master_product_count: 0,
    variant_count: 0,
    needs_review_count: 0
  })

  // Tree data
  const [categories, setCategories] = useState([])
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [editingId, setEditingId] = useState(null)

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false)
  const [addParentId, setAddParentId] = useState(null)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [creating, setCreating] = useState(false)

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const [statsRes, treeRes] = await Promise.all([
        api.get('/api/pim/stats'),
        api.get('/api/pim/categories/tree')
      ])
      setStats(statsRes)
      const cats = treeRes.categories || []
      setCategories(cats)
      // Start collapsed - user can expand as needed
      setExpandedIds(new Set())
    } catch (err) {
      console.error('Failed to load data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function handleToggleExpand(categoryId) {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  function expandAll() {
    const allIds = new Set()
    function collect(cats) {
      for (const cat of cats) {
        if (cat.children && cat.children.length > 0) {
          allIds.add(cat.category_id)
          collect(cat.children)
        }
      }
    }
    collect(categories)
    setExpandedIds(allIds)
  }

  function collapseAll() {
    setExpandedIds(new Set())
  }

  async function handleEdit(categoryId, newName) {
    setError(null)
    try {
      await api.patch(`/api/pim/categories/${categoryId}`, { name: newName })
      setSuccess('Category renamed successfully')
      await loadData()
    } catch (err) {
      console.error('Failed to rename category:', err)
      setError(err.message)
    }
  }

  function handleAddChild(parentId) {
    setAddParentId(parentId)
    setNewCategoryName('')
    setShowAddModal(true)
  }

  function handleAddTopLevel() {
    setAddParentId(null)
    setNewCategoryName('')
    setShowAddModal(true)
  }

  async function handleAddSubmit(e) {
    e.preventDefault()
    if (!newCategoryName.trim()) {
      setError('Category name is required')
      return
    }

    setCreating(true)
    setError(null)

    try {
      const payload = {
        name: newCategoryName.trim(),
        parent_id: addParentId
      }
      await api.post('/api/pim/categories', payload)
      setSuccess('Category created successfully')
      setShowAddModal(false)
      setNewCategoryName('')
      // Expand parent if we added a child
      if (addParentId) {
        setExpandedIds(prev => new Set([...prev, addParentId]))
      }
      await loadData()
    } catch (err) {
      console.error('Failed to create category:', err)
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  async function handleMove(categoryId, newParentId) {
    setError(null)
    try {
      await api.post(`/api/pim/categories/${categoryId}/move`, { new_parent_id: newParentId })
      setSuccess('Category moved successfully')
      await loadData()
    } catch (err) {
      console.error('Failed to move category:', err)
      setError(err.message)
    }
  }

  function handleDeleteClick(node) {
    setDeleteTarget(node)
    setShowDeleteModal(true)
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return

    setDeleting(true)
    setError(null)

    try {
      const response = await api.delete(`/api/pim/categories/${deleteTarget.category_id}`)
      const msg = response.products_affected > 0
        ? `Category deleted. ${response.products_affected} products lost their category.`
        : 'Category deleted successfully'
      setSuccess(msg)
      setShowDeleteModal(false)
      setDeleteTarget(null)
      await loadData()
    } catch (err) {
      console.error('Failed to delete category:', err)
      setError(err.message)
    } finally {
      setDeleting(false)
    }
  }

  function handleViewProducts(categoryId, categoryName) {
    // Navigate to products page with category filter
    navigate(`/pim/products?category_id=${categoryId}`)
  }

  // Count total categories
  function countCategories(cats) {
    let count = 0
    for (const cat of cats) {
      count++
      if (cat.children) {
        count += countCategories(cat.children)
      }
    }
    return count
  }

  const totalCategories = countCategories(categories)

  const metrics = [
    { label: 'Categories', value: totalCategories, color: 'blue' },
    { label: 'Master Products', value: stats.master_product_count, color: 'green' },
    { label: 'Variants', value: stats.variant_count, color: 'purple' },
    { label: 'Needs Review', value: stats.needs_review_count, color: stats.needs_review_count > 0 ? 'red' : '' }
  ]

  return (
    <>
      <MetricsRow metrics={metrics} />

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setError(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="card" style={{ background: 'var(--color-success-light)', padding: 'var(--space-4)', marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          {success}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddTopLevel}
          >
            + Add Top-Level Category
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={expandAll}
            disabled={categories.length === 0}
          >
            Expand All
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={collapseAll}
            disabled={categories.length === 0}
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Category Tree */}
      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Categories</div>
            <div className="empty-state-description">
              Click "Add Top-Level Category" to create your first category.
            </div>
          </div>
        ) : (
          <div className="category-tree">
            {categories.map(cat => (
              <CategoryNode
                key={cat.category_id}
                node={cat}
                depth={0}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                onEdit={handleEdit}
                onAddChild={handleAddChild}
                onMove={handleMove}
                onDelete={handleDeleteClick}
                onViewProducts={handleViewProducts}
                allCategories={categories}
                editingId={editingId}
                setEditingId={setEditingId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="modalOverlay">
          <div className="modalContent" onClick={e => e.stopPropagation()}>
            <div className="modalHead">
              <h2>{addParentId ? 'Add Child Category' : 'Add Top-Level Category'}</h2>
              <button
                type="button"
                className="modalClose"
                onClick={() => setShowAddModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="modalBody">
                <div className="filter-group">
                  <label className="filter-label">Category Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    placeholder="Enter category name..."
                    autoFocus
                    required
                  />
                </div>
              </div>
              <div className="modalFoot">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false)
                    setNewCategoryName('')
                  }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating || !newCategoryName.trim()}
                >
                  {creating ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="modalOverlay">
          <div className="modalContent" onClick={e => e.stopPropagation()}>
            <div className="modalHead">
              <h2>Delete Category</h2>
              <button
                type="button"
                className="modalClose"
                onClick={() => setShowDeleteModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modalBody">
              <p>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
              </p>
              {deleteTarget.children && deleteTarget.children.length > 0 && (
                <p className="text-warning" style={{ marginTop: '8px' }}>
                  Warning: This will also delete all child categories.
                </p>
              )}
              {deleteTarget.recursive_product_count > 0 && (
                <p className="text-warning" style={{ marginTop: '8px' }}>
                  {deleteTarget.recursive_product_count} product(s) will lose their category assignment.
                </p>
              )}
            </div>
            <div className="modalFoot">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeleteTarget(null)
                }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Component-specific styles */}
      <style>{`
        .category-tree {
          padding: var(--space-2);
        }
        .category-node {
          margin: 0;
        }
        .category-row {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-2) var(--space-3);
          border-radius: var(--radius-md);
          transition: background 0.15s ease;
        }
        .category-row:hover {
          background: var(--color-bg-hover);
        }
        .category-toggle {
          width: 24px;
          height: 24px;
          padding: 0;
          font-size: 10px;
          flex-shrink: 0;
          background: transparent;
          border: none;
          cursor: pointer;
          color: #666;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }
        .category-toggle:hover:not(:disabled) {
          background: rgba(0, 0, 0, 0.05);
          color: #333;
        }
        .category-toggle:disabled {
          cursor: default;
          opacity: 0.3;
        }
        .category-name {
          flex: 1;
          font-weight: 500;
          cursor: pointer;
        }
        .category-name:hover {
          text-decoration: underline;
        }
        .category-edit-form {
          flex: 1;
        }
        .category-edit-form .form-input {
          width: 100%;
        }
        .category-counts {
          flex-shrink: 0;
        }
        .category-actions {
          display: flex;
          gap: var(--space-1);
          opacity: 0;
          transition: opacity 0.15s ease;
        }
        .category-row:hover .category-actions {
          opacity: 1;
        }
        .dropdown-wrapper {
          position: relative;
        }
        .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          min-width: 180px;
          max-height: 200px;
          overflow-y: auto;
          background: #ffffff;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 100;
        }
        .dropdown-menu-up {
          top: auto;
          bottom: 100%;
        }
        .dropdown-item {
          padding: var(--space-2) var(--space-3);
          cursor: pointer;
          white-space: nowrap;
        }
        .dropdown-item:hover {
          background: var(--color-bg-hover);
        }
        .badge-clickable {
          cursor: pointer;
          transition: transform 0.1s ease, box-shadow 0.1s ease;
        }
        .badge-clickable:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        .text-warning {
          color: var(--color-warning);
        }
        .form-input-sm {
          padding: var(--space-1) var(--space-2);
          font-size: var(--text-sm);
        }
      `}</style>
    </>
  )
}

export default PIMCategories
